import {
  CURRENT_OEM_BRAND,
  IS_OEM,
  isOemStagingHostname,
  resolveOemBrandFromHostname,
  resolveOemExternalLoginUrl
} from '@/config/oem'

const API_PREFIX = '/api/v1'

const trimSlash = (s = '') => s.replace(/\/+$/, '')
const addLeadingSlash = (s = '') => (s ? (s[0] === '/' ? s : '/' + s) : '/')
const joinUrl = (base, path) => `${trimSlash(base)}${addLeadingSlash(path)}`

const isEnabled = (value) =>
  ['1', 'true', 'yes', 'on'].includes(
    String(value ?? '')
      .trim()
      .toLowerCase()
  )

// 维护模式在构建时注入。开启后由应用入口直接挂载维护页，跳过路由、
// 登录态恢复、埋点和业务组件初始化，避免维护期间产生任何接口请求。
export const SYSTEM_MAINTENANCE_ENABLED = isEnabled(
  import.meta.env.VITE_SYSTEM_MAINTENANCE_ENABLED ??
    import.meta.env.VITE_MAINTENANCE_MODE ??
    import.meta.env.VITE_MAINTENANCE_ENABLED
)

const resolveApiBaseUrl = () => {
  if (import.meta.env.DEV) return API_PREFIX

  const base = trimSlash(import.meta.env.VITE_API_BASE_URL || '')
  return base && !base.endsWith(API_PREFIX)
    ? joinUrl(base, API_PREFIX)
    : base || API_PREFIX
}

// 画布接口地址
export const API_BASE_URL = resolveApiBaseUrl()

// 临时暂停提示词优化；恢复需要前后端同时显式开启。
export const CANVAS_PROMPT_OPTIMIZATION_ENABLED =
  String(
    import.meta.env.VITE_CANVAS_PROMPT_OPTIMIZATION_ENABLED ??
      import.meta.env.VITE_PROMPT_OPTIMIZATION_ENABLED ??
      'false'
  )
    .trim()
    .toLowerCase() === 'true'

// 兼容现有业务模块使用的开关名称。
export const PROMPT_OPTIMIZATION_ENABLED = CANVAS_PROMPT_OPTIMIZATION_ENABLED

// 本地联调可覆盖后端登录地址；测试镜像留空时使用当前画布的 API。
export const BACKEND_LOGIN_ORIGIN = (
  import.meta.env.VITE_BACKEND_LOGIN_ORIGIN || ''
)
  .trim()
  .replace(/\/+$/, '')

// 跨 API 版本请求需要覆盖 Axios 实例的 `/api/v1` baseURL，避免被拼成
// `/api/v1/api/v2/...`。
export const API_V2_BASE_URL = joinUrl(
  API_BASE_URL.replace(/\/api\/v1$/, ''),
  '/api/v2'
)

// 外部登录地址（OEM 按品牌 workflowHomeUrl 解析，主站用环境变量）
export const EXTERNAL_LOGIN_URL = resolveOemExternalLoginUrl(
  import.meta.env.VITE_EXTERNAL_LOGIN_URL || ''
)

// 同一测试镜像服务并行环境，仅已接入浏览器回调合同的域名启用后端登录。
export const BACKEND_LOGIN_HOSTS = (
  import.meta.env.VITE_BACKEND_LOGIN_HOSTS || ''
)
  .split(',')
  .map((host) => host.trim().toLowerCase())
  .filter(Boolean)

// 绘梦与聚合平台首页地址（不用 OEM 解析后的登录 URL 作首页回退）
export const DREAMO_HOME_URL = (
  import.meta.env.VITE_DREAMO_HOME_URL ||
  import.meta.env.VITE_EXTERNAL_LOGIN_URL ||
  ''
).trim()
export const API_PLATFORM_HOME_URL = (
  import.meta.env.VITE_API_PLATFORM_HOME_URL || ''
).trim()

// 用户中心 Cookie 会话查询地址。该请求必须直接发往身份中心，不能
// 经过画布 API 代理，也不能把 tokenValue 交给前端。
export const IDENTITY_SESSION_URL = (
  import.meta.env.VITE_IDENTITY_SESSION_URL ||
  'https://platform.yunxiacn.com/api/auth/session'
).trim()

// 身份中心静默换票地址。
export const IDENTITY_SSO_REDIRECT_URL = (
  import.meta.env.VITE_IDENTITY_SSO_REDIRECT_URL ||
  'https://platform.yunxiacn.com/sso/getRedirectUrl'
).trim()

// 平台官网静态资源根地址
export const PLATFORM_BASE_URL = trimSlash(
  (import.meta.env.VITE_PLATFORM_BASE_URL || '').trim()
)
// 充值页地址
export const PAY_URL = (import.meta.env.VITE_PAY_URL || '').trim()

const resolveOemStagingWebSocketUrl = (configuredUrl) => {
  if (!IS_OEM || typeof window === 'undefined') return configuredUrl

  const hostname = String(window.location?.hostname || '')
    .trim()
    .toLowerCase()
  if (
    !isOemStagingHostname(hostname) ||
    resolveOemBrandFromHostname(hostname) !== CURRENT_OEM_BRAND
  ) {
    return configuredUrl
  }

  const host = String(window.location?.host || hostname).trim()
  if (!host) return configuredUrl

  const protocol = window.location?.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${host}/ws/connect`
}

// OEM 测试环境按当前画布域名连接，避免两个品牌共用 canvas-test 的 WebSocket。
export const WS_URL = resolveOemStagingWebSocketUrl(
  (import.meta.env.VITE_WS_URL || '').trim()
)

const resolveReportBaseUrl = () => {
  const configured = (import.meta.env.VITE_REPORT_BASE_URL || '').trim()
  if (configured) return configured

  return import.meta.env.PROD
    ? 'https://applogs.fachcloud.com'
    : 'https://devdata.muchcloud.com:8082'
}

// 埋点上报地址
export const REPORT_BASE_URL = resolveReportBaseUrl()

// 解析接口地址
export const resolveApiUrl = (path = '') => joinUrl(API_BASE_URL, path)
